<template>
  <div>
    <custom-dasboard
      ref="customDashboard"
      :is-mobile="isMobile"
      :sections="sections"
      :hide-graphics-view-more="!canShowGraphicsViewMore"
      @on-graphics-view-more="handleGraphicsViewMore"
    />
    <immediate-message-popup
      id="immediateMessagePopup"
      ref="_immediateMessagePopupRef"
      :items="immediateMessages"
      @message-marked-as-read="markMessageAsRead"
    />
  </div>
</template>
<script>
import mixAlert from '@/mixin/alert.mixin';
import CustomDasboard from 'custom-dashboard';
import { ImmediateMessagePopup } from '@/components/dashboard/inbox';

import sessionManager from '@/utils/managers/sessionManager';
import sessionKeys from '@/utils/constants/sessionKeys.js';
import messageCodes from '@/utils/constants/messageLocationCode.js';

import _service from '@/modules/dashboard/api.js';

import moment from 'moment';
import _ from 'lodash';

import TYPES from '@/utils/constants/operations-dashboard-management/scheduleItemTypes';
import APPS from '@/utils/constants/applications.js';
import { DASHBOARD_URL } from '@/config';

export default {
  name: 'Dashboard',
  components: {
    CustomDasboard,
    ImmediateMessagePopup,
  },
  mixins: [mixAlert],
  data() {
    return {
      immediateMessages: [],
      sections: [],
    };
  },
  computed: {
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    canShowGraphicsViewMore() {
      return this.userHasPermissions('menu.reports');
    },
  },
  methods: {
    async getSections() {
      const { data } = await _service.getSections();
      data.forEach((x) => {
        switch (x.id) {
          case TYPES.News:
            x.options = {
              data: [],
              load: this.getNews,
            };
            break;
          case TYPES.Event:
            x.options = {
              loading: false,
              pagination: {
                data: [],
                count: 0,
                totalItemCount: 0,
                currentPage: 0,
                pageCount: 1,
                pageSize: 4,
              },
              selectedDate: new Date(),
              routeToViewAll: 'calendarMain',
              load: this.getEvents,
            };
            break;
          case TYPES.Workshop:
            x.options = {
              count: null,
              data: [],
              currentPage: 1,
              pageCount: null,
              totalItemCount: null,
              routeTo: 'workshop',
              routeToViewAll: 'workshop',
              load: this.getWorkShops,
            };
            break;
          case TYPES.Message:
            x.options = {
              data: 0,
              routeToViewAll: 'dashboardMaintenance',
              load: this.getMessages,
            };
            break;
          case TYPES.TeachingMaterial:
            x.options = {
              count: null,
              data: [],
              currentPage: 1,
              pageCount: null,
              totalItemCount: null,
              routeTo: 'dashboardMaintenance',
              routeToViewAll: 'dashboardMaintenance',
              load: this.getTeachingMaterial,
            };
            break;
          case TYPES.EducationalMaterial:
            x.options = {
              count: 0,
              currentPage: 1,
              data: [],
              pageCount: 0,
              totalItemCount: 0,
              categories: [],
              load: this.getEducationalMaterials,
            };
            break;
          case TYPES.Graphic:
            x.options = {
              data: [],
              load: this.getGraphics,
              getDetails: this.getGraphicDetails,
            };
            break;
          case TYPES.Metrics:
            x.options = {
              data: { totalPowerDE: 0, totalEE: 0 },
              load: this.getMetrics,
            };
            break;
          case TYPES.Alerts:
            x.options = {
              pagination: {
                data: [],
                count: 0,
                totalItemCount: 0,
                currentPage: 0,
                pageCount: 1,
                pageSize: 4,
              },
              load: this.getAlerts,
            };
            break;
          default:
            x.options = {};
            break;
        }
        x.options = { ...x.options, appId: APPS.Mipe.toLowerCase() };
      });
      this.sections = data;
    },
    getOptions(id) {
      const section = this.sections.find((x) => x.id === id);
      return section?.options ?? {};
    },
    async getMetrics() {
      const options = this.getOptions(TYPES.Metrics);
      const { data } = await _service.getEnrollmentCount();
      options.data = data;
    },
    async getMessages() {
      const options = this.getOptions(TYPES.Message);
      const { data } = await _service.fetchUnreadMessages();
      options.data = data;
    },
    async getNews() {
      const options = this.getOptions(TYPES.News);
      const { data } = await _service.getNews();
      options.data = data;
    },
    async getEvents(currentPage = 1) {
      const options = this.getOptions(TYPES.Event);
      if (options.loading) return;
      const { pagination, selectedDate } = options;
      const { pageSize } = pagination;
      const date = moment(selectedDate)
        .startOf('day')
        .toDate();
      const payload = { date, currentPage, pageSize };
      options.loading = true;
      const { data } = await _service.getEvents(payload);
      options.loading = false;
      const values =
        currentPage > 1 ? pagination.data.concat(...data.data) : data.data;

      options.pagination = { ...data, data: values, pageSize };
    },
    async getWorkShops(currentPage = 1) {
      const options = this.getOptions(TYPES.Workshop);
      const payload = {
        currentPage,
        pageSize: 5,
        isFromDashboard: true,
      };
      const { data } = await _service.getWorkshops(payload);
      options.data = [...options.data, ...data.data];
      options.currentPage = data.currentPage;
      options.count = data.count;
      options.pageCount = data.pageCount;
      options.totalItemCount = data.totalItemCount;
    },
    async getTeachingMaterial(currentPage = 1) {
      const options = this.getOptions(TYPES.TeachingMaterial);
      const payload = {
        currentPage,
        pageSize: 5,
        isFromDashboard: true,
      };
      const { data } = await _service.getTeachingMaterial(payload);
      options.data = [...options.data, ...data.data];
      options.currentPage = data.currentPage;
      options.count = data.count;
      options.pageCount = data.pageCount;
      options.totalItemCount = data.totalItemCount;
    },
    async getAlerts(currentPage = 1) {
      const options = this.getOptions(TYPES.Alerts);
      const { pagination } = options;
      const { pageSize } = pagination;
      const payload = { currentPage, pageSize };
      const { data } = await _service.getAlerts(payload);
      options.pagination = { ...data, pageSize };
    },
    async getEducationalMaterials(currentPage = 1, categoryId = null) {
      const options = this.getOptions(TYPES.EducationalMaterial);
      const payload = { currentPage, pageSize: 3 };
      if (categoryId) payload.categoryId = categoryId;
      const { data } = await _service.getEducationalMaterials(payload);
      const values =
        currentPage > 1 ? options.data.concat(...data.data) : data.data;
      options.data = values;
      options.currentPage = data.currentPage;
      options.count = data.count;
      options.pageCount = data.pageCount;
      options.totalItemCount = data.totalItemCount;
    },
    async getEducationalMaterialCategories() {
      const options = this.getOptions(TYPES.EducationalMaterial);
      const { data } = await _service.getEducationalMaterialCategories();
      options.categories = data;
    },
    async markMessageAsRead(id) {
      await _service.saveReadMessage(id);
    },
    async getImmediateMessages() {
      const loaded = sessionManager.get(sessionKeys.KEY_MESSAGES_LOADED, false);
      if (loaded) {
        this.immediateMessages = sessionManager.get(
          sessionKeys.KEY_IMMEDIATE_MESSAGES,
          '[]'
        );
      } else {
        const { data } = await _service.fetchMessages();
        const messages = data.filter((s) =>
          [messageCodes.Inicio, messageCodes.InicioConAcuse].includes(
            s.messageLocationCode
          )
        );
        try {
          sessionManager.set(sessionKeys.KEY_IMMEDIATE_MESSAGES, messages);
          sessionManager.set(sessionKeys.KEY_MESSAGES_LOADED, true);
        } catch (e) {
          sessionManager.clear(sessionKeys.KEY_IMMEDIATE_MESSAGES);
          sessionManager.clear(sessionKeys.KEY_MESSAGES_LOADED);
        }
        this.immediateMessages = messages;
      }

      this.$nextTick(() => {
        if (this.immediateMessages.length > 0)
          this.$refs._immediateMessagePopupRef.init();
      });
    },
    async getGraphics() {
      const options = this.getOptions(TYPES.Graphic);
      const { data } = await _service.getGraphics();
      options.data = data.map((x) => ({
        ...x,
        active: false,
        detail: [],
      }));
    },
    async getGraphicDetails(reportId) {
      const { data } = await _service.getGraphicByReportType(reportId);
      return data;
    },
    handleGraphicsViewMore() {
      window.open(`${DASHBOARD_URL}/dashboard`, '_blank');
    },
  },
  async mounted() {
    Promise.all([this.getImmediateMessages(), this.getSections()]);
    if (this.isMobile) {
      const options = this.getOptions(TYPES.Alerts);
      if (options && options.pagination) {
        options.pagination.pageSize = 1;
      }
    }
  },
};
</script>
